import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Train, Clock, Ruler, MapPin, Shield } from 'lucide-react';
import './TrainDetailsPage.css';
import { fetchTrainByNumber } from '../api/trains';
import { fetchTrainSchedule, type ScheduleEntry } from '../api/stationsSchedules';
import CoachComposition from '../components/trains/CoachComposition';

interface TrainData {
    train_number: string;
    name: string;
    from_station_name: string;
    from_station_code: string;
    to_station_name: string;
    to_station_code: string;
    distance: number;
    duration_h: number;
    duration_m: number;
    zone: string;
    train_type: string;
    first_ac?: boolean;
    second_ac?: boolean;
    third_ac?: boolean;
    sleeper?: boolean;
    chair_car?: boolean;
    first_class?: boolean;
    geometry?: { type: string; coordinates: number[][] };
}

const CLASS_MAP = [
    { key: 'first_ac', id: '1A', label: 'First AC' },
    { key: 'second_ac', id: '2A', label: 'Second AC' },
    { key: 'third_ac', id: '3A', label: 'Third AC' },
    { key: 'sleeper', id: 'SL', label: 'Sleeper' },
    { key: 'chair_car', id: 'CC', label: 'Chair Car' },
    { key: 'first_class', id: 'FC', label: 'First Class' },
] as const;

const TrainDetailsPage: React.FC = () => {
    const { trainNumber } = useParams<{ trainNumber: string }>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [train, setTrain] = useState<TrainData | null>(null);
    const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);

    useEffect(() => {
        const load = async () => {
            if (!trainNumber) return;
            try {
                const [trainData, scheduleData] = await Promise.all([
                    fetchTrainByNumber(trainNumber),
                    fetchTrainSchedule(trainNumber),
                ]);
                setTrain(trainData);
                setSchedule(scheduleData?.data ?? []);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [trainNumber]);

    if (loading) {
        return (
            <div className="tdp-state">
                <div className="tdp-spinner" />
                <span>Loading train details…</span>
            </div>
        );
    }

    if (!train) {
        return (
            <div className="tdp-state">
                <Train size={32} strokeWidth={1.5} />
                <p>Train not found.</p>
                <button className="tdp-btn-primary" onClick={() => navigate(-1)}>Go Back</button>
            </div>
        );
    }

    const availableClasses = CLASS_MAP.filter(c => train[c.key as keyof TrainData]);

    return (
        <div className="tdp-root">

            {/* Top Navigation Bar */}
            <header className="tdp-topbar">
                <button className="tdp-back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={18} />
                    Back
                </button>
                <div className="tdp-topbar-meta">
                    <span className="tdp-train-num">#{train.train_number}</span>
                    <span className="tdp-type-tag">
                        <Shield size={12} />
                        {train.train_type || 'Express'}
                    </span>
                </div>
            </header>

            {/* Hero Section */}
            <section className="tdp-hero">
                <div className="tdp-train-num">#{train.train_number} • {train.train_type}</div>
                <h1 className="tdp-train-name">{train.name}</h1>

                <div className="tdp-route">
                    <div className="tdp-station">
                        <div className="tdp-stn-code">{train.from_station_code}</div>
                        <div className="tdp-stn-name">{train.from_station_name}</div>
                    </div>

                    <div className="tdp-route-line" />

                    <div className="tdp-station">
                        <div className="tdp-stn-code">{train.to_station_code}</div>
                        <div className="tdp-stn-name">{train.to_station_name}</div>
                    </div>
                </div>
            </section>

            {/* High-Density Color Accent Metrics Grid */}
            <section className="tdp-metrics">
                <div className="tdp-metric">
                    <Clock size={24} />
                    <div>
                        <strong>{train.duration_h || 0}h {train.duration_m || 0}m</strong>
                        <span>Duration</span>
                    </div>
                </div>
                <div className="tdp-metric">
                    <Ruler size={24} />
                    <div>
                        <strong>{train.distance ? `${train.distance} km` : '—'}</strong>
                        <span>Distance</span>
                    </div>
                </div>
                <div className="tdp-metric">
                    <MapPin size={24} />
                    <div>
                        <strong>{train.zone || '—'}</strong>
                        <span>Zone</span>
                    </div>
                </div>
                <div className="tdp-metric">
                    <Train size={24} />
                    <div>
                        <strong>{schedule.length}</strong>
                        <span>Stops</span>
                    </div>
                </div>
            </section>

            {/* Panel Layout Split */}
            <div className="tdp-layout">

                {/* Left Side: Timeline Rail Schedule */}
                <div className="tdp-main-panel">
                    <div className="tdp-panel-head">
                        <h2>Journey Schedule</h2>
                        <span>{schedule.length} Stops</span>
                    </div>

                    <div className="tdp-composition-section" style={{ marginBottom: '2rem' }}>
                        <CoachComposition 
                            trainNumber={train.train_number}
                            classes={availableClasses.map(c => c.id)}
                        />
                    </div>

                    <div className="tdp-timeline">
                        {schedule.map((stop, idx) => {
                            const isFirst = idx === 0;
                            const isLast = idx === schedule.length - 1;

                            return (
                                <div
                                    key={stop._id || idx}
                                    className="tdp-stop-row"
                                    onClick={() => navigate(`/stations/${stop.station_code}`)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') navigate(`/stations/${stop.station_code}`);
                                    }}
                                >
                                    <div className="tdp-track">
                                        {!isFirst && <div className="tdp-track-line" />}
                                        <div className={`tdp-track-dot ${isFirst ? 'source' : isLast ? 'destination' : ''}`} />
                                        {!isLast && <div className="tdp-track-line" />}
                                    </div>

                                    <div className="tdp-stop-content">
                                        <div className="tdp-stop-header">
                                            <span className="tdp-stop-name">{stop.station_name}</span>
                                            <span className="tdp-stop-code">{stop.station_code}</span>
                                        </div>

                                        <div className="tdp-stop-times">
                                            <div>
                                                <label>Arrival</label>
                                                <span>{stop.arrival || '—'}</span>
                                            </div>
                                            <div>
                                                <label>Departure</label>
                                                <span>{stop.departure || '—'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="tdp-day">D{stop.day}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Right Side: Information Panels */}
                <aside className="tdp-sidebar">

                    {/* Dynamic Auto-Loop Accommodation Class Cards */}
                    <div className="tdp-card">
                        <h3>Available Classes</h3>
                        {availableClasses.length > 0 ? (
                            <div className="tdp-classes-grid">
                                {availableClasses.map(cls => (
                                    <div key={cls.id} className="tdp-class-card">
                                        <span>{cls.id}</span>
                                        <small>{cls.label}</small>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="tdp-muted">No class information available.</p>
                        )}
                    </div>

                    {/* Telemetry Information Metadata */}
                    <div className="tdp-card">
                        <h3>Train Information</h3>
                        <div className="tdp-info-row">
                            <span>Type</span>
                            <strong>{train.train_type}</strong>
                        </div>
                        <div className="tdp-info-row">
                            <span>Zone</span>
                            <strong>{train.zone}</strong>
                        </div>
                        <div className="tdp-info-row">
                            <span>Distance</span>
                            <strong>{train.distance} km</strong>
                        </div>
                        <div className="tdp-info-row">
                            <span>Duration</span>
                            <strong>{train.duration_h}h {train.duration_m}m</strong>
                        </div>
                    </div>

                    {/* Route Map Action Stub */}
                    {train.geometry?.coordinates?.length ? (
                        <div className="tdp-card">
                            <h3>Route Map</h3>
                            <div className="tdp-map-stub">
                                <MapPin size={20} />
                                <span>Interactive route map</span>
                                <button className="tdp-btn-primary">View Full Map</button>
                            </div>
                        </div>
                    ) : null}

                </aside>
            </div>
        </div>
    );
};

export default TrainDetailsPage;